import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ArrowLeft, Settings, ShieldCheck, AlertTriangle, RefreshCw, LogOut, ExternalLink, MailPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useGmailApi, useGmailHealth, useGmailOAuth } from '../hooks/useGmailApi';

const Container = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing[6]};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: ${({ theme }) => theme.spacing[5]};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const Header = styled.header`
  max-width: 920px;
  width: 100%;
  margin: 0 0 ${({ theme }) => theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: fit-content;

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};

  h1 {
    margin: 0;
    font-size: clamp(24px, 3vw, 32px);
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  p {
    margin: 0;
    font-size: 15px;
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: 1.6;
    max-width: 68ch;
  }
`;

const StatusGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[4]};
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
`;

const StatusCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius['2xl']};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  padding: ${({ theme }) => theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const StatusHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const IconBadge = styled.div<{ $variant?: 'active' | 'warning' | 'danger' }>`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $variant }) => {
    if ($variant === 'active') return `${theme.colors.success}15`;
    if ($variant === 'warning') return `${theme.colors.warning}15`;
    return `${theme.colors.error}15`;
  }};
  border: 1px solid ${({ theme, $variant }) => {
    if ($variant === 'active') return `${theme.colors.success}60`;
    if ($variant === 'warning') return `${theme.colors.warning}60`;
    return `${theme.colors.error}60`;
  }};
  color: ${({ theme, $variant }) => {
    if ($variant === 'active') return theme.colors.success;
    if ($variant === 'warning') return theme.colors.warning;
    return theme.colors.error;
  }};
`;

const StatusText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  span {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const StatusBadge = styled.span<{ $variant: 'success' | 'warning' | 'danger' }>`
  align-self: flex-start;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  background: ${({ theme, $variant }) => {
    if ($variant === 'success') return `${theme.colors.success}20`;
    if ($variant === 'warning') return `${theme.colors.warning}20`;
    return `${theme.colors.error}20`;
  }};
  color: ${({ theme, $variant }) => {
    if ($variant === 'success') return theme.colors.success;
    if ($variant === 'warning') return theme.colors.warning;
    return theme.colors.error;
  }};
  border: 1px solid ${({ theme, $variant }) => {
    if ($variant === 'success') return `${theme.colors.success}50`;
    if ($variant === 'warning') return `${theme.colors.warning}50`;
    return `${theme.colors.error}50`;
  }};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'danger' | 'ghost' }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${({ theme, $variant }) => {
    if ($variant === 'primary') return theme.colors.primary;
    if ($variant === 'danger') return theme.colors.error;
    return theme.colors.border;
  }};
  background: ${({ theme, $variant }) => {
    if ($variant === 'primary') return theme.colors.primary;
    if ($variant === 'danger') return `${theme.colors.error}15`;
    return 'transparent';
  }};
  color: ${({ theme, $variant }) => {
    if ($variant === 'primary') return '#ffffff';
    if ($variant === 'danger') return theme.colors.error;
    return theme.colors.textSecondary;
  }};

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    transition: transform 0.2s ease;
  }

  svg.spinning {
    animation: spin 0.9s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const Section = styled.section`
  max-width: 920px;
  width: 100%;
  margin: ${({ theme }) => `${theme.spacing[6]} 0`};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Steps = styled.ol`
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};

  li {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: 1.6;

    a {
      color: ${({ theme }) => theme.colors.primary};
      font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    strong {
      color: ${({ theme }) => theme.colors.textPrimary};
    }
  }
`;

const InfoGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[4]};
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  max-width: 920px;
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const InfoCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  padding: ${({ theme }) => theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.textPrimary};
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
  }

  p {
    margin: 0;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: 1.6;

    a {
      color: ${({ theme }) => theme.colors.primary};
      font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const HealthIndicator = styled.div<{ $healthy: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background: ${({ $healthy, theme }) => ($healthy ? `${theme.colors.success}15` : `${theme.colors.error}15`)};
  border: 1px solid ${({ $healthy, theme }) => ($healthy ? theme.colors.success : theme.colors.error)};

  .indicator-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $healthy, theme }) => ($healthy ? theme.colors.success : theme.colors.error)};
    ${({ $healthy }) =>
      $healthy &&
      `
      animation: pulse 2s ease-in-out infinite;

      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
    `}
  }

  span {
    font-size: 12px;
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ $healthy, theme }) => ($healthy ? theme.colors.success : theme.colors.error)};
  }
`;

const StatusDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
`;

const GmailConfig: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const userId = user?.uid;

  const {
    hasToken,
    tokenEmail,
    tokenActive,
    tokenExpired,
    isLoadingTokenConfig,
    refetchTokenConfig,
    disconnectAccount,
    isDeletingToken,
  } = useGmailApi(userId);

  const { getAuthUrl } = useGmailOAuth(userId);
  const { data: healthData, isLoading: isLoadingHealth } = useGmailHealth();

  const accountStatusVariant = useMemo<'success' | 'warning' | 'danger'>(() => {
    if (!hasToken) return 'danger';
    if (tokenExpired) return 'warning';
    if (tokenActive) return 'success';
    return 'warning';
  }, [hasToken, tokenActive, tokenExpired]);

  const accountStatusText = useMemo(() => {
    if (!hasToken) return 'Conta desconectada';
    if (tokenExpired) return 'Token expirado';
    if (tokenActive) return 'Conta ativa';
    return 'Aguardando validação';
  }, [hasToken, tokenActive, tokenExpired]);

  const isHealthy = healthData?.status === 'success';

  const handleDisconnect = async () => {
    const confirmed = window.confirm('Tem certeza que deseja desconectar sua conta Gmail do Loopra?');
    if (!confirmed) return;

    try {
      await disconnectAccount();
      await refetchTokenConfig();
    } catch (error) {
      console.error('[GmailConfig] Erro ao desconectar conta Gmail', error);
    }
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/gmail')}>
          <ArrowLeft size={16} />
          Voltar para Integração Gmail
        </BackButton>

        <TitleGroup>
          <h1>Configurar Integração Gmail</h1>
          <p>
            Conecte sua conta Google com OAuth2 para sincronizar mensagens, etiquetas e enviar e-mails diretamente pelo Loopra — na maioria dos casos basta fazer login e autorizar o acesso.
            O Loopra solicita somente os escopos necessários; você pode revogar o acesso a qualquer momento pelo Google.
          </p>
        </TitleGroup>
      </Header>

      <StatusGrid>
        <StatusCard>
          <StatusHeader>
            <IconBadge $variant={accountStatusVariant === 'success' ? 'active' : accountStatusVariant === 'warning' ? 'warning' : 'danger'}>
              <MailPlus size={24} />
            </IconBadge>
            <StatusText>
              <h2>Status da Conta</h2>
              <span>{tokenEmail || 'Nenhuma conta conectada'}</span>
            </StatusText>
          </StatusHeader>
          <StatusBadge $variant={accountStatusVariant}>{accountStatusText}</StatusBadge>

          <Actions>
            <ActionButton
              $variant="primary"
              onClick={() => getAuthUrl()}
              disabled={isLoadingTokenConfig}
            >
              <Settings size={16} />
              {hasToken ? 'Reautorizar Conta' : 'Conectar Conta Gmail'}
            </ActionButton>

            {hasToken && (
              <ActionButton
                $variant="danger"
                onClick={handleDisconnect}
                disabled={isDeletingToken || isLoadingTokenConfig}
              >
                <LogOut size={16} />
                Desconectar
              </ActionButton>
            )}

            <ActionButton
              $variant="ghost"
              onClick={() => refetchTokenConfig()}
              disabled={isLoadingTokenConfig}
            >
              <RefreshCw size={16} className={isLoadingTokenConfig ? 'spinning' : ''} />
              Atualizar status
            </ActionButton>
          </Actions>
        </StatusCard>

        <StatusCard>
          <StatusHeader>
            <IconBadge $variant={isHealthy ? 'active' : 'warning'}>
              <ShieldCheck size={24} />
            </IconBadge>
            <StatusText>
              <h2>Saúde do Serviço</h2>
              <span>Monitoramento do conector Gmail</span>
            </StatusText>
          </StatusHeader>

          <HealthIndicator $healthy={isHealthy ?? false}>
            <div className="indicator-dot" />
            <span>
              {isLoadingHealth ? 'Verificando...' : isHealthy ? 'Serviço online' : 'Serviço indisponível'}
            </span>
          </HealthIndicator>

          <StatusDescription>
            O conector Gmail roda com redundância e monitora limites da API do Google. Em caso de instabilidade, tente reautorizar ou aguarde alguns minutos.
          </StatusDescription>
        </StatusCard>
      </StatusGrid>

      <Section>
        <SectionTitle>Como configurar</SectionTitle>
        <Steps>
          <li>
            Clique em <strong>Conectar Conta Gmail</strong> e selecione a conta Google que deseja vincular ao Loopra.
          </li>
          <li>
            Conceda as permissões solicitadas pelo Google para leitura e envio de e-mails. O fluxo padrão usa as credenciais gerenciadas pelo Loopra.
          </li>
          <li>
            Após autorizar, a conta ficará vinculada automaticamente. Use o botão de <strong>Atualizar status</strong> se precisar forçar uma verificação.
          </li>
          {/* Observação avançada removida: credenciais customizadas não são disponibilizadas diretamente. */}
        </Steps>
      </Section>

      <InfoGrid>
        <InfoCard>
          <h3>
            <ShieldCheck size={16} />
            Segurança e escopos
          </h3>
          <p>
            O Loopra solicita apenas os escopos estritamente necessários para leitura e envio de e-mails. Tokens são armazenados de forma criptografada apenas para permitir as ações autorizadas. Você pode revogar o acesso a qualquer momento pelo painel de segurança do Google.
          </p>
        </InfoCard>

        <InfoCard>
          <h3>
            <AlertTriangle size={16} />
            Limites do Gmail
          </h3>
          <p>
            O Gmail possui cotas diárias para envio e leitura. Se atingir um limite, as ações afetadas ficarão em espera até a renovação automática; na maior parte dos casos basta aguardar alguns minutos.
          </p>
        </InfoCard>

        <InfoCard>
          <h3>
            <ExternalLink size={16} />
            Documentação
          </h3>
          <p>
            Não disponibilizamos configurações avançadas de credenciais ou alertas no Google Cloud. Consulte a documentação apenas para referências de integração e entre em contato com o suporte se precisar de ajuda.
          </p>
        </InfoCard>
      </InfoGrid>
    </Container>
  );
};

export default GmailConfig;
